import React, { Component } from 'react';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import sortBy from 'lodash/sortBy';
import Tooltip from 'rc-tooltip';

import '../../../../styles/UserScoreboard.css';

import Avatar from '../../../../components/Avatar';
import NoElements from '../../../../components/states/NoElements';

const ScoreboardWrapper = styled.div`
  display: flex;
  align-self: center;
  justify-content: space-between;
  flex: none;
  width: 100%;
  font-family: Arial, sans-serif;
  font-size: 14px;
  border: 1px solid rgba(0, 0, 0, 0.125);
  padding: 0.5rem 0.75rem;
  margin-bottom: -1px;
`;

export default class UserRanking extends Component {
  constructor(props) {
    super(props);

    const scores = this.computeScore();

    this.state = {
      scores: scores,
      groupedScores: this.groupScores(scores),
      view: props.scoreboard ? true : false,
    };
  }

  componentWillReceiveProps = nextProps => {
    if (nextProps.users.size !== this.props.users.size) {
      const scores = this.computeScore(nextProps.users);

      this.setState({
        scores: scores,
        groupedScores: this.groupScores(scores),
      });
    }

    if (nextProps.scoreboard !== this.props.scoreboard) {
      this.setState({
        view: nextProps.scoreboard ? true : false,
      });
    }
  };

  determineScore = score => {
    if (score >= 5 && score < 10) return { grade: 'F', color: 'red' };
    else if (score >= 10 && score < 20) return { grade: 'D', color: 'orange' };
    else if (score >= 20 && score < 30)
      return { grade: 'C', color: 'goldenrod' };
    else if (score >= 30 && score < 40) return { grade: 'B', color: 'blue' };
    else if (score >= 40 && score < 50) return { grade: 'A', color: 'green' };
    else if (score >= 50) return { grade: 'A+', color: 'green' };
    else
      return {
        grade: 'No Scoring Data Available',
        color: 'black',
        size: 14,
        hideTooltip: true,
        fontWeight: 700,
      };
  };

  groupScores = scores => {
    return sortBy(scores, s => s.score.grade).reduce((prev, cur) => {
      const curScoreGrade = cur.score.grade;
      const currentScoreObj = prev[curScoreGrade] || {
        count: 0,
        data: [],
        color: '#fff',
      };
      currentScoreObj.count = currentScoreObj.count + 1;
      currentScoreObj.data.push(cur.username);
      currentScoreObj.color = cur.score.color;
      prev[curScoreGrade] = currentScoreObj;
      return prev;
    }, {});
  };

  computeScore = userOverride => {
    const { scores, users } = this.props;

    const usersData = userOverride || users;

    return Object.entries(
      scores.reduce((prev, cur) => {
        const user = prev[cur.responsibility] || {
          score: 0,
          count: 0,
          bonuses: {},
          deductions: {},
        };

        user.count = user.count + 1;
        user.score = parseInt(cur.score, 10) + user.score;

        const penalties = Object.keys(cur.data.penalties);
        const bonuses = Object.keys(cur.data.bonuses);

        penalties.forEach(penalty => {
          const count = user.deductions[penalty] || 0;

          user.deductions[penalty] = count + 1;
        });

        bonuses.forEach(bonus => {
          const count = user.bonuses[bonus] || 0;

          user.bonuses[bonus] = count + 1;
        });

        prev[cur.responsibility] = user;
        return prev;
      }, {}),
    ).map((obj, scoreObject) => {
      const objData = obj[1];
      const userId = obj[0];
      const user = usersData.get(userId, Map());
      objData.rawScore = Math.floor(objData.score / objData.count);
      objData.score = this.determineScore(objData.score / objData.count);
      objData.username = `${user.get('first_name')} ${user.get('last_name')}`;
      objData.user = user;
      return objData;
    });
  };

  renderScoreboard = scores => {
    return (
      <div
        className="panel-content-item"
        style={{
          overflowY: 'auto',
          display: 'flex',
          flex: '1 1 0%',
          flexDirection: 'column',
          paddingBottom: 1,
        }}
      >
        {sortBy(scores, s => -s.rawScore).map((data, k) => {
          return (
            <ScoreboardWrapper className="scoreboard-item" key={k}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  marginRight: 10,
                }}
              >
                <Avatar
                  className="mr-1"
                  avatar={data.user.avatar}
                  user={data.user}
                  width="37"
                  height="37"
                  rounded
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flex: 1,
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                {data.username}
              </div>
              <Tooltip placement="left" overlay={data.rawScore}>
                <div
                  style={{
                    display: 'flex',
                    flex: 'none',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                    color: '#707070',
                    fontWeight: '700',
                  }}
                >
                  {data.score.grade}
                </div>
              </Tooltip>
            </ScoreboardWrapper>
          );
        })}
      </div>
    );
  };

  renderGroupedScores = groupedScores => {
    const highestValue = Object.entries(groupedScores).reduce((p, c) => {
      const val = c[1].count;

      if (val > p) return val;
      return p;
    }, 0);

    return (
      <div style={{
        display: 'flex',
        flex: 1,
        overflowY: 'auto',
        flexDirection: 'column',
        border: '1px solid rgba(0,0,0,0.125)'
      }}>
        <div className="user-scoreboard chart">
          {Object.entries(groupedScores).map((data, k) => {
            const score = data[1];
            return (
              <li className="user-scoreboard" key={k}>
                <Tooltip
                  key={k}
                  placement="left"
                  overlay={score.data.map(u => <div>{u}</div>)}
                >
                  <span
                    style={{
                      height: `${(score.count / highestValue) * 100}%`,
                      background: score.color,
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '14px',
                      color: '#707070',
                      fontWeight: '700',
                    }}
                    title={data[0]}
                  />
                </Tooltip>
              </li>
            );
          })}
        </div>
      </div>
    );
  };

  changeState = () => {
    this.setState({
      view: !this.state.view,
    });
  };

  determineTitle = view => (view ? 'User Scoreboard' : 'Grouped Ranking');
  determineIcon = view => (view ? 'fa fa-bar-chart' : 'fa fa-bars');

  render() {
    const { groupedScores, scores, view } = this.state;

    if (!scores.length) {
      return <NoElements missingType="scores" dashboard />;
    }

    return (
      <React.Fragment>
        {view
          ? scores && this.renderScoreboard(scores)
          : groupedScores && this.renderGroupedScores(groupedScores)}
      </React.Fragment>
    );
  }
}

UserRanking.propTypes = {
  scores: PropTypes.array,
};



// WEBPACK FOOTER //
// ./src/views/Project/TaskHome/UsersScoreboard/UsersScoreboard.js